import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import {
  useCreateBeer,
  useGenerateBeerDescriptions,
  usePatchBeer,
  useSuggestStyles,
  useSuggestTags,
} from '../hooks/useBeers';
import { useSession } from '../lib/session';
import { useToast } from '../lib/toast';
import '../styles/new-beer.css';

function parseAbv(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n;
}

function normalizeTag(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

export function NewBeerPage(): React.ReactElement {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasRole } = useSession();

  const canCreate = hasRole('BarManager', 'Brewer', 'Leader', 'Admin');

  const createBeer = useCreateBeer();
  const patchBeer = usePatchBeer();
  const generateDescriptions = useGenerateBeerDescriptions();

  const [draftBeerId, setDraftBeerId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [style, setStyle] = useState('');
  const [abv, setAbv] = useState('');
  const [menuDescription, setMenuDescription] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  const [brewProcess, setBrewProcess] = useState('');
  const [ingredients, setIngredients] = useState('');
  const [namingStory, setNamingStory] = useState('');
  const [tastingNotes, setTastingNotes] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');

  const styleSuggestions = useSuggestStyles(style).data?.suggestions ?? [];
  const tagSuggestions = useSuggestTags(tagInput).data?.suggestions ?? [];

  const canGenerate = useMemo(() => {
    return (
      name.trim().length > 0
      && style.trim().length > 0
      && (
        brewProcess.trim().length > 0
        || ingredients.trim().length > 0
        || namingStory.trim().length > 0
        || tastingNotes.trim().length > 0
        || additionalContext.trim().length > 0
      )
    );
  }, [name, style, brewProcess, ingredients, namingStory, tastingNotes, additionalContext]);

  const isBusy = createBeer.isPending || patchBeer.isPending || generateDescriptions.isPending;

  function pushTag(raw: string): void {
    const next = normalizeTag(raw);
    if (!next) return;
    if (tags.some((t) => t.toLowerCase() === next.toLowerCase())) return;
    setTags((prev) => [...prev, next]);
    setTagInput('');
  }

  function removeTag(tag: string): void {
    setTags((prev) => prev.filter((t) => t !== tag));
  }

  async function ensureDraft(): Promise<string> {
    if (draftBeerId) return draftBeerId;
    if (!name.trim() || !style.trim()) {
      throw new Error('Name and style are required before creating a draft.');
    }

    const created = await createBeer.mutateAsync({
      name: name.trim(),
      style: style.trim(),
      abv: parseAbv(abv),
      menuDescription: menuDescription.trim() || null,
      description: description.trim() || null,
      tags,
      status: 'active',
    });

    setDraftBeerId(created.id);
    toast.info('Draft beer created.');
    return created.id;
  }

  async function saveAll(showSuccessToast: boolean): Promise<void> {
    const beerId = await ensureDraft();
    await patchBeer.mutateAsync({
      beerId,
      body: {
        name: name.trim(),
        style: style.trim(),
        abv: parseAbv(abv),
        menuDescription: menuDescription.trim() || null,
        description: description.trim() || null,
        tags,
      },
    });
    if (showSuccessToast) toast.success('Beer saved.');
  }

  async function onGenerateDescriptions(): Promise<void> {
    try {
      await ensureDraft();
      const generated = await generateDescriptions.mutateAsync({
        beerName: name.trim(),
        beerStyle: style.trim(),
        brewerNotes: {
          brewProcess: brewProcess.trim() || null,
          ingredients: ingredients.trim() || null,
          namingStory: namingStory.trim() || null,
          tastingNotes: tastingNotes.trim() || null,
          additionalContext: additionalContext.trim() || null,
        },
      });

      setMenuDescription(generated.menuDescription);
      setDescription(generated.description);
      toast.success('Descriptions generated. Review and edit as needed.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not generate descriptions.';
      toast.error(msg);
    }
  }

  async function onSaveAndExit(): Promise<void> {
    try {
      await saveAll(true);
      navigate('/beers');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save beer.';
      toast.error(msg);
    }
  }

  if (!canCreate) {
    return (
      <div className="new-beer-page new-beer-page-denied">
        <div className="new-beer-denied-card">
          <h1 className="new-beer-denied-title">Access denied</h1>
          <p className="new-beer-denied-copy">
            You need Bar Manager, Brewer, Leader, or Admin role to add beers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="new-beer-page">
      <div className="new-beer-header">
        <div>
          <div className="uppercase-label new-beer-catalog-label">Catalog</div>
          <h1 className="new-beer-title">Add Beer</h1>
          <p className="new-beer-subtitle">
            Create a draft, add menu + staff descriptions, and generate copy from brewer notes.
          </p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigate('/beers')} disabled={isBusy}>
          Back to Beers
        </button>
      </div>

      <div className="new-beer-sections">
        <section className="new-beer-card">
          <div className="new-beer-core-grid">
            <label className="new-beer-field">
              <span className="uppercase-label">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Cerberus" />
            </label>
            <label className="new-beer-field">
              <span className="uppercase-label">Style</span>
              <input
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="West Coast IPA"
                list="style-suggestions"
              />
              <datalist id="style-suggestions">
                {styleSuggestions.map((s) => (
                  <option key={s.style} value={s.style} />
                ))}
              </datalist>
            </label>
            <label className="new-beer-field">
              <span className="uppercase-label">ABV</span>
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={abv}
                onChange={(e) => setAbv(e.target.value)}
                placeholder="6.8"
              />
            </label>
          </div>

          <div className="new-beer-tags-wrap">
            <span className="uppercase-label">Tags</span>
            <div className="new-beer-tags-list">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="new-beer-tag-chip"
                >
                  {tag}
                  <Icon name="close" size={12} />
                </button>
              ))}
            </div>
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  pushTag(tagInput);
                }
              }}
              placeholder="Type a tag and press Enter"
            />
            {tagSuggestions.length > 0 && (
              <div className="new-beer-tag-suggestions">
                {tagSuggestions.map((s) => (
                  <button
                    key={s.tag}
                    type="button"
                    onClick={() => pushTag(s.tag)}
                    className="new-beer-tag-suggestion-chip"
                  >
                    {s.tag} ({s.count})
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="new-beer-card">
          <div className="new-beer-generator-head">
            <div className="uppercase-label new-beer-generator-label">AI Description Generator</div>
            <div className="new-beer-generator-copy">
              Provide brewing notes. The generator creates both a menu headline blurb and a longer bartender-facing description.
            </div>
          </div>

          <div className="new-beer-notes-grid">
            <textarea value={brewProcess} onChange={(e) => setBrewProcess(e.target.value)} placeholder="How was it brewed?" rows={3} />
            <textarea value={ingredients} onChange={(e) => setIngredients(e.target.value)} placeholder="Ingredients and grain/hop bill details" rows={3} />
            <textarea value={namingStory} onChange={(e) => setNamingStory(e.target.value)} placeholder="Why is it named this way?" rows={2} />
            <textarea value={tastingNotes} onChange={(e) => setTastingNotes(e.target.value)} placeholder="Aromas, flavor, finish, and mouthfeel" rows={3} />
            <textarea value={additionalContext} onChange={(e) => setAdditionalContext(e.target.value)} placeholder="Any other context for guests and staff" rows={2} />
          </div>

          <div className="new-beer-generator-actions">
            <button className="btn btn-primary" disabled={!canGenerate || isBusy} onClick={onGenerateDescriptions}>
              {generateDescriptions.isPending ? 'Generating...' : 'Generate Descriptions'}
            </button>
          </div>
        </section>

        <section className="new-beer-card">
          <label className="new-beer-field new-beer-field-gap">
            <span className="uppercase-label">Menu Description (2-3 short sentences)</span>
            <textarea
              value={menuDescription}
              onChange={(e) => setMenuDescription(e.target.value)}
              placeholder="Short guest-facing copy for menus"
              rows={3}
            />
          </label>

          <label className="new-beer-field">
            <span className="uppercase-label">Full Description (Bartender Notes)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Longer, richer guidance for bartenders and guests"
              rows={6}
            />
          </label>
        </section>

        <div className="new-beer-footer-actions">
          <button className="btn btn-ghost" disabled={isBusy} onClick={() => navigate('/beers')}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={isBusy} onClick={onSaveAndExit}>
            {isBusy ? 'Saving...' : 'Save Beer'}
          </button>
        </div>
      </div>
    </div>
  );
}
